import React, { useState } from 'react'
import { Moment } from 'moment/moment'
import { useMutation } from '@apollo/client'
import { Button } from '@resiliatech/birdsong'
import ADD_GOAL from 'Apollo/Mutations/AddGoal'
import { ASSIGN_GOALS_TO_GOAL_GROUP } from 'Apollo/Mutations/AssignGoalsToGoalGroup'
import UPSERT_CHECKLIST_ITEM from 'Apollo/Mutations/UpsertChecklistItem'
import CREATE_AUTOMATED_GOAL from 'Apollo/Mutations/CreateAutomatedGoal'
import {
  AddGoalInput,
  GoalCategory,
  GoalImpactCategory,
  GoalType,
  NonprofitOrg,
  TemplateLibrary,
} from 'Generated/graphql-types'
import {
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { NonprofitOrgSearch } from './NonprofitOrgSearch'
import { Row, SelectContainer, Text } from './FunderSharedGoals.styled'
import { CreateGoalType, TaskToCreate } from './constants'
import { CustomGoalCreation } from './CustomGoalCreation'
import { TaskCreation } from './TaskCreation'
import { TemplateGoalCreation } from './TemplateGoalCreation'
import { GoalCreationContainer } from './GoalCreation.styled'

const initialGoalValues = {
  category: GoalCategory.Other,
  description: '',
  endDate: null,
  startDate: null,
  title: '',
  type: GoalType.UserCustom,
  impactCategory: GoalImpactCategory.Organizational,
}

type GoalCreationProps = {
  popError: (message: string) => void
  popSuccess: (message: string) => void
}

export const GoalCreation = ({ popError, popSuccess }: GoalCreationProps) => {
  const [goalType, setGoalType] = useState(CreateGoalType.Custom)
  const [goalGroupId, setGoalGroupId] = useState('')
  const [tasks, setTasks] = useState<TaskToCreate[]>([])
  const [goalToCreate, setGoalToCreate] =
    useState<AddGoalInput>(initialGoalValues)
  const [templateToCreate, setTemplateToCreate] =
    useState<TemplateLibrary | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [funderOrgId, setFunderOrgId] = useState<number | null>(null)
  const [nonprofitOrgs, setNonprofitOrgs] = useState<NonprofitOrg[]>([])

  const [addGoal, { loading: addGoalLoading }] = useMutation(ADD_GOAL)
  const [upsertChecklistItem, { loading: tasksLoading }] = useMutation(
    UPSERT_CHECKLIST_ITEM
  )
  const [createAutomatedGoal, { loading: autoGoalLoading }] = useMutation(
    CREATE_AUTOMATED_GOAL
  )

  const [assignGoalsToGoalGroup, { loading: assignGoalsToGoalGroupLoading }] =
    useMutation(ASSIGN_GOALS_TO_GOAL_GROUP)

  const handleResetForm = () => {
    setGoalToCreate(initialGoalValues)
    setGoalType(CreateGoalType.Custom)
    setNonprofitOrgs([])
    setSearchValue('')
    setTasks([])
    setTemplateToCreate(null)
    setFunderOrgId(null)
    setGoalGroupId('')
  }

  const handleAddTask = (updates: Record<string, string>) => {
    const { id, linkText, linkUrl, resourceId, title } = updates
    const task = { id, linkText, linkUrl, resourceId, title }
    setTasks([...tasks, task])
  }

  const handleSearchNonprofitOrgs = (
    funderId: number,
    nonprofits: NonprofitOrg[]
  ) => {
    setFunderOrgId(funderId)
    setNonprofitOrgs(nonprofits)
  }

  const handleDeleteTask = (taskId: string) => {
    const filteredTasks = tasks.filter((task) => task.id !== taskId)
    setTasks(filteredTasks)
  }

  const handleUpdateGoal = (
    updates: Record<string, string | Moment | null>
  ) => {
    setGoalToCreate({ ...goalToCreate, ...updates })
  }

  const handleCreateGoalWithTasks = async () => {
    const { category, title, type, impactCategory } = goalToCreate

    if (!category || !title || !type || !impactCategory) {
      popError('Ensure all required fields are filled out before submitting')
      return
    }

    if (goalGroupId && Number.isNaN(Number(goalGroupId))) {
      popError('Please enter a valid goal group ID')
      return
    }

    try {
      const addGoalsResponse = await Promise.all(
        nonprofitOrgs.map((n) =>
          addGoal({
            variables: {
              goalInfo: {
                ...goalToCreate,
                shareWithFunderOrgId: funderOrgId,
                isGoalSharedByFunder: true,
                nonprofitOrgId: n.id,
                impactCategory,
              },
            },
          })
        )
      )

      const goals = addGoalsResponse.map((r) => r.data?.addGoal)

      const tasksPromises = goals.map(async (goal) =>
        Promise.all(
          tasks.map((task) =>
            upsertChecklistItem({
              variables: {
                itemInfo: {
                  goalId: goal.id,
                  linkText: task.linkText,
                  linkUrl: task.linkUrl,
                  resourceId: task.resourceId,
                  title: task.title,
                },
                nonprofitOrgId: goal.nonprofitOrgId,
              },
            })
          )
        )
      )

      const tasksResponse = await Promise.all(tasksPromises)
      const numGoalsRequested = goals.length
      const numTasksRequested = goals.length * tasks.length
      const goalIdsCreated = goals.filter((g) => !!g).map((g) => g.id)
      const numGoalsCreated = goalIdsCreated.length
      const numTasksCreated = tasksResponse
        .flat()
        .filter((t) => !!t.data).length

      if (goalGroupId) {
        await assignGoalsToGoalGroup({
          variables: {
            data: {
              goalGroupId: Number(goalGroupId),
              goalIds: goalIdsCreated.map((id) => Number(id)),
            },
          },
        })
      }

      const goalGroupIdMessage = `Goal group ID: ${goalGroupId} `

      const tasksCountMessage =
        numTasksRequested > 0
          ? ` and ${numTasksCreated} of ${numTasksRequested} tasks`
          : ''

      const feedbackMessage = `Successfully created ${numGoalsCreated} of ${numGoalsRequested} custom goals${tasksCountMessage}! ${goalGroupIdMessage}\nGoal IDs: ${goalIdsCreated.join(
        ','
      )}`
      popSuccess(feedbackMessage)
      console.log(feedbackMessage)
      setGoalToCreate(initialGoalValues)
      setTasks([])
    } catch {
      popError('Failed to create goal')
    }
  }

  const handleCreateTemplateGoal = async () => {
    if (!templateToCreate) {
      popError('Please select a template to create')
      return
    }

    if (goalGroupId && Number.isNaN(Number(goalGroupId))) {
      popError('Please enter a valid goal group ID')
      return
    }

    try {
      const autoCreatedGoalPromises = nonprofitOrgs.map(async (n) =>
        createAutomatedGoal({
          variables: {
            appcuesFlowId: templateToCreate.appcuesFlowId,
            templateId: templateToCreate.id,
            nonprofitOrgId: n.id,
            shareWithFunderOrgId: funderOrgId,
            isGoalSharedByFunder: true,
            impactCategory: goalToCreate.impactCategory,
          },
        })
      )
      const autoCreatedGoals = await Promise.all(autoCreatedGoalPromises)
      const numGoalsRequested = nonprofitOrgs.length
      const goalIdsCreated = autoCreatedGoals
        .filter((g) => !!g.data)
        .map((g) => g.data?.createAutomatedGoal?.goal.id)
      const numGoalsCreated = goalIdsCreated.length

      if (goalGroupId) {
        await assignGoalsToGoalGroup({
          variables: {
            data: {
              goalGroupId: Number(goalGroupId),
              goalIds: goalIdsCreated.map((id) => Number(id)),
            },
          },
        })
      }

      const successMessage = `Successfully created ${numGoalsCreated} of ${numGoalsRequested} template goals! `
      const goalIdMessage = `Goal IDs: ${goalIdsCreated.join(',')}`
      const goalGroupIdMessage = goalGroupId
        ? `Goal group ID: ${goalGroupId} `
        : ``

      const feedbackMessage = `${successMessage}${goalGroupIdMessage}${goalIdMessage}`
      popSuccess(feedbackMessage)
      console.log(feedbackMessage)
    } catch {
      popError(
        `Failed to create goals for all nonprofit org IDs. Some inserts may have occurred.`
      )
    }
  }

  const hasNonprofitOrgIds = !!nonprofitOrgs.length
  const isValidCustomGoal =
    goalToCreate.title && goalToCreate.category && goalToCreate.impactCategory
  const isValidTemplateGoal =
    !!templateToCreate && !!goalToCreate.impactCategory
  const isCreatingCustomGoal = goalType === CreateGoalType.Custom
  const isLoading =
    addGoalLoading ||
    tasksLoading ||
    autoGoalLoading ||
    assignGoalsToGoalGroupLoading

  const createGoalButtonLabel = isCreatingCustomGoal
    ? 'Create Custom Goal'
    : 'Create Template Goal'

  const onCreateGoalClick = isCreatingCustomGoal
    ? handleCreateGoalWithTasks
    : handleCreateTemplateGoal

  const isCreateGoalsButtonDisabled = isCreatingCustomGoal
    ? !isValidCustomGoal
    : !isValidTemplateGoal

  return (
    <GoalCreationContainer>
      <NonprofitOrgSearch
        nonprofitOrgs={nonprofitOrgs}
        onChangeSearchValue={setSearchValue}
        onClear={handleResetForm}
        onSearch={handleSearchNonprofitOrgs}
        searchValue={searchValue}
      />
      {hasNonprofitOrgIds && (
        <>
          <Paper className='overflow-hidden'>
            <TableContainer className='max-h-[300px]'>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Nonprofit Org Id</TableCell>
                    <TableCell>Nonprofit Org Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nonprofitOrgs.map((org, index) => (
                    <TableRow key={org.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{org.id}</TableCell>
                      <TableCell>{org.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Divider />
          <Text>
            Now select the type of goal you&apos;d like to create for your
            nonprofit organizations:
          </Text>
          <Row>
            <Text>Goal Type:</Text>
            <SelectContainer>
              <FormControl>
                <InputLabel>Goal Type</InputLabel>
                <Select
                  displayEmpty
                  label={CreateGoalType.Custom}
                  onChange={({ target: { value } }) =>
                    setGoalType(value as CreateGoalType)
                  }
                  required
                  sx={{ minWidth: '200px' }}
                  value={goalType}
                >
                  <MenuItem value={CreateGoalType.Custom}>
                    {CreateGoalType.Custom} Goal
                  </MenuItem>
                  <MenuItem value={CreateGoalType.Template}>
                    {CreateGoalType.Template} Goal
                  </MenuItem>
                </Select>
              </FormControl>
            </SelectContainer>
          </Row>
          <Row>
            <Text>Impact Category:</Text>
            <SelectContainer>
              <FormControl>
                <InputLabel>Impact Category</InputLabel>
                <Select
                  displayEmpty
                  label='Impact Category'
                  onChange={({ target: { value } }) =>
                    handleUpdateGoal({
                      impactCategory: value as GoalImpactCategory,
                    })
                  }
                  required
                  sx={{ minWidth: '200px' }}
                  value={
                    goalToCreate.impactCategory ||
                    GoalImpactCategory.Organizational
                  }
                >
                  <MenuItem value={GoalImpactCategory.Mission}>
                    {GoalImpactCategory.Mission}
                  </MenuItem>
                  <MenuItem value={GoalImpactCategory.Organizational}>
                    {GoalImpactCategory.Organizational}
                  </MenuItem>
                </Select>
              </FormControl>
            </SelectContainer>
          </Row>
          {goalType === CreateGoalType.Custom && (
            <>
              <CustomGoalCreation
                goalToCreate={goalToCreate}
                onUpdateGoal={handleUpdateGoal}
              />
              <TaskCreation
                onAddTask={handleAddTask}
                onDeleteTask={handleDeleteTask}
                tasks={tasks}
              />
            </>
          )}
          {goalType === CreateGoalType.Template && (
            <TemplateGoalCreation
              onSelectTemplate={(template) => setTemplateToCreate(template)}
            />
          )}
          <Row>
            <Text>Goal Group ID:</Text>
            <TextField
              id='goalGroupId'
              label='Goal Group ID (optional; use if you want to add to an existing goal group)'
              onChange={({ target: { value } }) => setGoalGroupId(value)}
              size='small'
              value={goalGroupId}
              variant='outlined'
              sx={{ minWidth: '700px' }}
            />
          </Row>
          <Row>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Button
                isDisabled={isCreateGoalsButtonDisabled}
                label={createGoalButtonLabel}
                onClick={onCreateGoalClick}
                type='button'
                variant='primary'
              />
            )}
            <Button
              label='Clear All'
              onClick={handleResetForm}
              type='button'
              variant='secondary'
            />
          </Row>
        </>
      )}
    </GoalCreationContainer>
  )
}
